<script setup lang="ts">
import { computed } from "vue";
import * as Sentry from "@sentry/vue";
import { useToast } from "vue-toastification";
import { Download } from "@lucide/vue";

import { getDownloadReport } from "@/api/services/Emission";
import { downloadBlob } from "@/services/Utils/downloadBlob";
import { Report } from "@/types/Views/ProjectDashboard/Report";
import { reportProvenanceLabel } from "@/services/Utils/reportProvenance";

const props = defineProps<{
  report: Report;
  buildingProjectId: string;
}>();

const toast = useToast();

// Which state the file describes (RTLCA-5280). Without it, two exports a week apart off two
// different save points read as the same report twice, and reopening one had no answer to "is this
// still what the project looks like?".
const provenance = computed(() => reportProvenanceLabel(props.report));

const downloadReport = async () => {
  try {
    const response = await getDownloadReport(props.report.id, props.buildingProjectId);
    const blob = new Blob([response], { type: props.report.magic });

    downloadBlob(blob, props.report.blobPath);

    toast.success("Report downloaded");
  } catch (error) {
    // Previously unguarded: a failed request left the row looking like nothing had happened.
    toast.error("Could not download the report");
    Sentry.captureException(error);
  }
};
</script>

<template>
  <!--
    A button rather than a clickable div, which is what this was: the row could not be reached by
    Tab, did not fire on Enter, and had no focus ring. The filename truncates instead of widening
    the flyout, so the accessible name carries it in full.

    min-h-[44px] because this list is also mounted in MobileMenu, where p-2 alone left a 32px touch
    target. items-start rather than items-center now that the row is two lines.
  -->
  <button
    type="button"
    class="focus-ring interactive-active duration-fast ease-standard rounded-4 t-xs text-type-default flex min-h-[44px] w-full items-start gap-2 px-3 py-2 text-left transition-colors hover:bg-slate-100"
    :aria-label="`Download ${report.blobPath}, exported from ${provenance}`"
    @click="downloadReport">
    <Download
      class="icon-sm text-type-muted mt-px-2 shrink-0"
      :stroke-width="1.5" />
    <span class="flex min-w-0 flex-col">
      <span class="truncate">{{ report.blobPath }}</span>
      <!-- aria-hidden: the button's own label already reads the provenance out, so announcing it
           again here would repeat it on every row. -->
      <span
        class="t-footnote text-type-muted truncate"
        aria-hidden="true">
        {{ provenance }}
      </span>
    </span>
  </button>
</template>
